import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, CardContent, Typography } from '@pesto/ui';
import { DobField } from '@pesto/ui/components/Forms/DobField';
import { TextField } from '@pesto/ui/components/Forms/TextField';
import { formatDate } from '@pesto/utils';
import { Edit, Save, User, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntityHeader } from '../../EntityProfile/components/EntityHeader';

export interface UserPersonalInfoUser {
  id: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  dob: string | null;
  insertedAt: string | null;
  isFriendsAndFam: boolean | null;
  lithicAccountToken?: string | null;
}

export interface UserPersonalInfoFormValues {
  firstName: string;
  lastName: string;
  middleName?: string;
  phone: string;
  dob: string;
  isFriendsAndFam?: boolean;
}

type UserPersonalInfoProps = {
  user: UserPersonalInfoUser;
} & (
  | { readOnly: true; onSave?: never; saving?: never }
  | {
      readOnly?: false;
      onSave: (values: UserPersonalInfoFormValues) => void | Promise<void>;
      saving?: boolean;
    }
);

const stringSchema = (field: string) => z.string().max(255, `${field} is too long`);

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  middleName: z.string().optional(),
  phone: z.string().min(1, 'Phone is required'),
  isFriendsAndFam: z.boolean().optional(),
  dob: stringSchema('Date of birth')
    .regex(/^\d{2}-\d{2}-\d{4}$/, 'Invalid date format')
    .min(1, `Date of birth is required`),
});

const buildDefaults = (user: UserPersonalInfoUser): UserPersonalInfoFormValues => ({
  firstName: user.firstName ?? '',
  lastName: user.lastName ?? '',
  middleName: user.middleName ?? '',
  phone: user.phone ?? '',
  dob: user.dob ?? '',
  isFriendsAndFam: user.isFriendsAndFam ?? false,
});

export const UserPersonalInfo = (props: UserPersonalInfoProps) => {
  const { user, readOnly } = props;
  const [editMode, setEditMode] = useState(false);

  const {
    register,
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<UserPersonalInfoFormValues>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: buildDefaults(user),
  });

  useEffect(() => {
    reset(buildDefaults(user));
  }, [user, reset]);

  const formData = watch();

  const handleSave = async (values: UserPersonalInfoFormValues) => {
    if (readOnly) return;

    try {
      await props.onSave(values);
      setEditMode(false);
    } catch (error) {
      console.error('Error saving personal info', error);
    }
  };

  const handleCancel = () => {
    reset(buildDefaults(user));
    setEditMode(false);
  };

  const inputsReadOnly = readOnly || !editMode;

  const editButtons = readOnly ? null : editMode ? (
    <>
      <Button
        onClick={(event) => void handleSubmit(handleSave)(event)}
        disabled={(props.saving ?? false) || !isDirty}
        size="sm"
        className="gap-1"
      >
        <Save className="h-3 w-3" />
        {props.saving ? 'Saving...' : 'Save'}
      </Button>
      <Button variant="outline" onClick={handleCancel} size="sm" className="gap-1">
        <X className="h-3 w-3" />
        Cancel
      </Button>
    </>
  ) : (
    <Button onClick={() => setEditMode(true)} variant="outline" size="sm" className="gap-1">
      <Edit className="h-3 w-3" />
      Edit
    </Button>
  );

  return (
    <Card>
      <EntityHeader
        config={{
          avatar: { icon: User },
          title: 'Personal Information',
          description: 'User profile and contact details',
          actions: editButtons,
        }}
      />

      {!readOnly && editMode && (
        <div className="px-6 pb-4">
          <Typography variant="body" className="text-muted-foreground text-sm">
            Update user information below. Changes will be saved immediately.
          </Typography>
        </div>
      )}

      <CardContent>
        <form className="space-y-6" onSubmit={(event) => void handleSubmit(handleSave)(event)}>
          <div className="space-y-4">
            <Typography
              variant="headerSmall"
              className="text-muted-foreground text-sm font-medium uppercase tracking-wider"
            >
              System Information
            </Typography>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField label="User ID" value={user.id ?? ''} readOnly placeholder="N/A" />
              <TextField
                label="Account Created"
                value={formatDate(user.insertedAt ?? undefined) || ''}
                readOnly
                placeholder="N/A"
              />
              {user.lithicAccountToken && (
                <TextField label="Lithic Account Token" value={user.lithicAccountToken} readOnly />
              )}
            </div>
          </div>

          <div className="space-y-4">
            <Typography
              variant="headerSmall"
              className="text-muted-foreground text-sm font-medium uppercase tracking-wider"
            >
              Personal Details
            </Typography>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                {...register('firstName')}
                label="First Name"
                value={formData.firstName || ''}
                readOnly={inputsReadOnly}
                error={errors.firstName?.message}
                placeholder="Enter first name"
              />
              <TextField
                {...register('lastName')}
                label="Last Name"
                value={formData.lastName || ''}
                readOnly={inputsReadOnly}
                error={errors.lastName?.message}
                placeholder="Enter last name"
              />
              <TextField
                {...register('middleName')}
                label="Middle Name"
                value={formData.middleName || ''}
                readOnly={inputsReadOnly}
                error={errors.middleName?.message}
                placeholder="Enter middle name (optional)"
              />
              <DobField
                label="Date of Birth"
                value={formData.dob || ''}
                readOnly={inputsReadOnly}
                error={errors.dob?.message}
                placeholder="MM-DD-YYYY"
                onChange={(value) => {
                  if (!inputsReadOnly) setValue('dob', value);
                }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <Typography
              variant="headerSmall"
              className="text-muted-foreground text-sm font-medium uppercase tracking-wider"
            >
              Contact Information
            </Typography>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                {...register('phone')}
                label="Phone Number"
                value={formData.phone || ''}
                readOnly={inputsReadOnly}
                error={errors.phone?.message}
                placeholder="Enter phone number"
              />
              <TextField label="Email Address" value={user.email ?? ''} readOnly placeholder="N/A" />
            </div>
          </div>

          {Object.keys(errors).length > 0 && (
            <div className="border-destructive/20 bg-destructive/5 rounded-lg border p-4">
              <Typography variant="headerSmall" className="text-destructive mb-2 text-sm font-medium">
                Please fix the following errors:
              </Typography>
              {Object.entries(errors).map(([key, error]) => (
                <Typography variant="body" className="text-destructive text-sm" key={key}>
                  • {error?.message}
                </Typography>
              ))}
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
